'use client'

import {
  Smartphone,
  CreditCard,
  Palette,
  Users,
  Wifi,
  TrendingUp,
} from 'lucide-react'

/**
 * Southeast Asia UX Design Diagrams
 * Visual components for SEA SaaS localization blog
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

// 1. SEA Digital Economy Snapshot — sourced stats grid
export function SEADigitalEconomyDiagram() {
  const stats = [
    {
      value: '$300B+',
      label: 'SEA digital economy GMV (2025)',
      source: 'Google-Temasek-Bain',
      color: '#008dcc',
      icon: TrendingUp,
    },
    {
      value: '92%',
      label: 'of SEA e-commerce sales via mobile',
      source: 'TMO Group',
      color: '#F90B8A',
      icon: Smartphone,
    },
    {
      value: '5.2%',
      label: 'of websites meet basic WCAG standards',
      source: 'Accessibility.Works',
      color: '#FDC304',
      icon: Users,
    },
    {
      value: '57%',
      label: 'of PH retail transactions are digital',
      source: 'Ken Research, 2024',
      color: '#8b5cf6',
      icon: CreditCard,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          SOUTHEAST ASIA DIGITAL LANDSCAPE
        </h3>
        <p className='text-sm text-muted-foreground'>
          Why SaaS UX must be designed for this market, not adapted from Western
          defaults
        </p>
      </div>

      <div className='grid grid-cols-2 gap-3'>
        {stats.map((stat, idx) => {
          const Icon = stat.icon
          return (
            <div
              key={idx}
              className='p-4 rounded-xl border text-center'
              style={{
                backgroundColor: `${stat.color}08`,
                borderColor: `${stat.color}30`,
              }}
            >
              <Icon
                className='w-5 h-5 mx-auto mb-2'
                style={{ color: stat.color }}
              />
              <p
                className='text-2xl md:text-3xl font-bold'
                style={{ color: stat.color }}
              >
                {stat.value}
              </p>
              <p className='text-[10px] text-muted-foreground mt-1 leading-tight'>
                {stat.label}
              </p>
              <p className='text-[9px] text-muted-foreground mt-1.5'>
                {stat.source}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// 2. Color Psychology Map — cultural color meanings by country
export function CulturalColorMapDiagram() {
  const countries = [
    {
      name: 'Philippines',
      flag: '🇵🇭',
      colors: ['Bright, vibrant hues', 'Energy & celebration'],
      cta: 'Blue/Green CTAs',
      accent: '#008dcc',
    },
    {
      name: 'Thailand',
      flag: '🇹🇭',
      colors: ['Purple & Gold', 'Royalty & wealth'],
      cta: 'Gold premium tiers',
      accent: '#8b5cf6',
    },
    {
      name: 'Vietnam',
      flag: '🇻🇳',
      colors: ['Red = luck/prosperity', 'Effective for CTAs'],
      cta: 'Red CTAs convert',
      accent: '#ef4444',
    },
    {
      name: 'Singapore',
      flag: '🇸🇬',
      colors: ['Minimalist, corporate', 'Professional tones'],
      cta: 'Clean & neutral',
      accent: '#6b7280',
    },
    {
      name: 'Indonesia',
      flag: '🇮🇩',
      colors: ['Warm, approachable', 'Friendly & vibrant'],
      cta: 'Warm orange/green',
      accent: '#FDC304',
    },
    {
      name: 'Malaysia',
      flag: '🇲🇾',
      colors: ['Business-forward', 'Trust signals matter'],
      cta: 'Blue/teal tones',
      accent: '#0ea5e9',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          COLOR PSYCHOLOGY ACROSS ASEAN MARKETS
        </h3>
        <p className='text-sm text-muted-foreground'>
          The same color can build trust in one market and create friction in
          another
        </p>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-3 gap-3'>
        {countries.map((country, idx) => (
          <div
            key={idx}
            className='p-3 rounded-xl border'
            style={{
              backgroundColor: `${country.accent}08`,
              borderColor: `${country.accent}25`,
            }}
          >
            <div className='flex items-center gap-2 mb-2'>
              <span className='text-lg'>{country.flag}</span>
              <span className='text-sm font-bold text-foreground'>
                {country.name}
              </span>
            </div>
            {country.colors.map((c, i) => (
              <p
                key={i}
                className='text-[10px] text-muted-foreground leading-tight'
              >
                {c}
              </p>
            ))}
            <div
              className='mt-2 px-2 py-1 rounded text-[10px] font-medium'
              style={{
                backgroundColor: `${country.accent}15`,
                color: country.accent,
              }}
            >
              {country.cta}
            </div>
          </div>
        ))}
      </div>

      <p className='text-[10px] text-muted-foreground text-center mt-4'>
        <Palette className='w-3 h-3 inline mr-1' />
        Always validate with local A/B testing — cultural preferences evolve
      </p>
    </div>
  )
}

// 3. Payment Ecosystem by Country
export function PaymentEcosystemDiagram() {
  const markets = [
    {
      region: 'Philippines',
      flag: '🇵🇭',
      primary: 'GCash (89% wallet share)',
      secondary: 'Maya, COD, Bank transfer',
      digital: '57% digital transactions',
      source: 'Thunes/Ken Research',
      color: '#008dcc',
    },
    {
      region: 'Indonesia',
      flag: '🇮🇩',
      primary: 'OVO, GoPay, DANA',
      secondary: 'COD still significant, Bank transfer',
      digital: 'Fastest wallet growth in SEA',
      source: 'e-Conomy SEA 2024',
      color: '#FDC304',
    },
    {
      region: 'Thailand & Vietnam',
      flag: '🇹🇭🇻🇳',
      primary: 'QR code payments, Bank transfer',
      secondary: 'PromptPay (TH), VNPay (VN)',
      digital: 'QR dominant at point of sale',
      source: 'GSMA 2024',
      color: '#8b5cf6',
    },
    {
      region: 'Singapore & Malaysia',
      flag: '🇸🇬🇲🇾',
      primary: 'GrabPay (35-38% share)',
      secondary: 'Credit cards, PayNow/DuitNow',
      digital: 'Highest card adoption in SEA',
      source: 'CoinLaw/Kadence',
      color: '#22c55e',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          PAYMENT ECOSYSTEMS BY MARKET
        </h3>
        <p className='text-sm text-muted-foreground'>
          One checkout flow does not fit all — each market has dominant payment
          methods
        </p>
      </div>

      <div className='space-y-3'>
        {markets.map((market, idx) => (
          <div
            key={idx}
            className='p-4 rounded-xl border'
            style={{
              backgroundColor: `${market.color}05`,
              borderColor: `${market.color}25`,
            }}
          >
            <div className='flex items-center justify-between mb-2'>
              <div className='flex items-center gap-2'>
                <span className='text-base'>{market.flag}</span>
                <span className='text-sm font-bold text-foreground'>
                  {market.region}
                </span>
              </div>
              <CreditCard className='w-4 h-4' style={{ color: market.color }} />
            </div>
            <div className='grid grid-cols-2 gap-2'>
              <div>
                <p className='text-[10px] text-muted-foreground'>Primary</p>
                <p className='text-xs text-foreground'>{market.primary}</p>
              </div>
              <div>
                <p className='text-[10px] text-muted-foreground'>Secondary</p>
                <p className='text-xs text-foreground'>{market.secondary}</p>
              </div>
            </div>
            <div className='mt-2 flex items-center justify-between'>
              <p
                className='text-[10px] font-medium'
                style={{ color: market.color }}
              >
                {market.digital}
              </p>
              <p className='text-[9px] text-muted-foreground'>
                {market.source}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// 4. Mobile Infrastructure Reality — speeds & coverage
export function MobileInfrastructureDiagram() {
  const countries = [
    {
      name: 'Singapore',
      speed: 159,
      bar: 100,
      coverage: '99%+ 4G',
      color: '#22c55e',
    },
    {
      name: 'Vietnam',
      speed: 152,
      bar: 95,
      coverage: '99.8% 4G',
      color: '#008dcc',
    },
    {
      name: 'Malaysia',
      speed: 150,
      bar: 94,
      coverage: '35% 5G',
      color: '#8b5cf6',
    },
    {
      name: 'Thailand',
      speed: 117,
      bar: 73,
      coverage: '95% 5G (AIS)',
      color: '#FDC304',
    },
    {
      name: 'Philippines',
      speed: 58,
      bar: 36,
      coverage: '100+ cities 5G',
      color: '#F90B8A',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          MOBILE INTERNET SPEEDS ACROSS SEA
        </h3>
        <p className='text-sm text-muted-foreground'>
          A 3x speed gap between Singapore and the Philippines means your SaaS
          must be performance-optimized
        </p>
      </div>

      <div className='space-y-3'>
        {countries.map((country, idx) => (
          <div key={idx} className='flex items-center gap-3'>
            <span className='text-xs font-medium text-foreground w-24 text-right flex-shrink-0'>
              {country.name}
            </span>
            <div className='flex-1 relative'>
              <div className='h-6 bg-muted rounded-full overflow-hidden'>
                <div
                  className='h-full rounded-full flex items-center justify-end pr-2 transition-all'
                  style={{
                    width: `${country.bar}%`,
                    backgroundColor: `${country.color}30`,
                    borderRight: `3px solid ${country.color}`,
                  }}
                >
                  <span
                    className='text-[10px] font-bold'
                    style={{ color: country.color }}
                  >
                    {country.speed} Mbps
                  </span>
                </div>
              </div>
            </div>
            <span className='text-[9px] text-muted-foreground w-20 flex-shrink-0'>
              {country.coverage}
            </span>
          </div>
        ))}
      </div>

      <div className='mt-4 p-3 rounded-lg bg-[#F90B8A]/5 border border-[#F90B8A]/20'>
        <div className='flex items-center gap-2'>
          <Wifi className='w-4 h-4 text-[#F90B8A]' />
          <p className='text-[10px] text-muted-foreground'>
            <strong className='text-foreground'>Design implication:</strong>{' '}
            Core features must work on 2G/3G. Use progressive enhancement,
            aggressive caching, and adaptive image loading.
          </p>
        </div>
      </div>

      <p className='text-[10px] text-muted-foreground text-center mt-3'>
        Sources: Seasia.co, Opensignal, GSMA 2024
      </p>
    </div>
  )
}
